/**
 * Canonical YouTube walkthrough video catalog for landing and product pages.
 */

export type WalkthroughVideoKey =
  | 'seaWolf2026'
  | 'redRock2026'
  | 'casey2026'
  | 'mckAiCoach'
  | 'seaWolfSolver'
  | 'ecosystem2025';

export type LandingTrack = 'all' | 'mck' | 'bcg';

export type WalkthroughVideo = {
  key: WalkthroughVideoKey;
  youtubeId: string;
  title: string;
  description: string;
  productPath: string;
  productLabel: string;
  pillLabel: string;
};

export const COGNIDOOR_YOUTUBE_CHANNEL = 'https://www.youtube.com/@Cogni-door';

export const WALKTHROUGH_VIDEOS: Record<WalkthroughVideoKey, WalkthroughVideo> = {
  seaWolf2026: {
    key: 'seaWolf2026',
    youtubeId: 'fBnGriXD7TU',
    title: 'McKinsey Solve Sea-Wolf Game 2026: Full Step-by-Step Walkthrough',
    description: 'Walk through the Sea-Wolf assessment format, decision logic, and how to practise with Cognidoor.',
    productPath: '/seawolf-practice',
    productLabel: 'Sea Wolf Simulation',
    pillLabel: 'Sea Wolf',
  },
  redRock2026: {
    key: 'redRock2026',
    youtubeId: '7e1HS4q17UA',
    title: 'McKinsey Red Rock (2026): Full Walkthrough + Strategy Guide',
    description: 'See how Red Rock cases flow from investigation through report, graph, and mini-cases.',
    productPath: '/redrock',
    productLabel: 'Red Rock Simulator',
    pillLabel: 'Red Rock',
  },
  casey2026: {
    key: 'casey2026',
    youtubeId: '6CnsAgyB4WM',
    title: 'BCG Casey Assessment (2026) Explained: Full Walkthrough and Tips',
    description: 'Understand the Casey format, timing, exhibits, and how to turn each run into better prep.',
    productPath: '/bcg-casey-simulation',
    productLabel: 'Casey Simulation',
    pillLabel: 'BCG Casey',
  },
  mckAiCoach: {
    key: 'mckAiCoach',
    youtubeId: 'q3DQO0brG5I',
    title: 'McKinsey Case Interview With an AI Coach: Full Walkthrough',
    description: 'Watch a full McKinsey-style case session with interviewer-led flow and structured coach feedback.',
    productPath: '/mckinsey-ai-coach',
    productLabel: 'McKinsey AI Coach',
    pillLabel: 'AI Coach',
  },
  seaWolfSolver: {
    key: 'seaWolfSolver',
    youtubeId: 'rZjsPN6ZEaQ',
    title: 'MCKINSEY SEA WOLF GAME (2025) | Beat the Game with Cognidoor Solver',
    description: 'See how the Cognidoor Sea Wolf solver helps you work through microbe selection faster.',
    productPath: '/seawolf',
    productLabel: 'Sea Wolf Guide & Solver',
    pillLabel: 'Solver',
  },
  ecosystem2025: {
    key: 'ecosystem2025',
    youtubeId: '8cSWUWmhFqo',
    title: "Beat McKinsey's Ecosystem Game (2025) | with Cognidoor",
    description: 'Learn the Ecosystem game structure and how Cognidoor helps you solve scenarios step by step.',
    productPath: '/ecosystem-solver',
    productLabel: 'Ecosystem Solver',
    pillLabel: 'Ecosystem',
  },
};

export const LANDING_VIDEOS_BY_TRACK: Record<LandingTrack, readonly WalkthroughVideoKey[]> = {
  all: ['seaWolf2026', 'redRock2026', 'casey2026', 'mckAiCoach'],
  mck: ['seaWolf2026', 'redRock2026', 'mckAiCoach'],
  bcg: ['casey2026'],
};

export function getWalkthroughVideo(key: WalkthroughVideoKey): WalkthroughVideo {
  return WALKTHROUGH_VIDEOS[key];
}

export function getLandingVideosForTrack(track: LandingTrack): WalkthroughVideo[] {
  return LANDING_VIDEOS_BY_TRACK[track].map((key) => WALKTHROUGH_VIDEOS[key]);
}

export function buildYouTubeEmbedUrl(youtubeId: string, options?: { autoplay?: boolean }): string {
  const params = new URLSearchParams({ rel: '0', modestbranding: '1' });
  if (options?.autoplay) {
    params.set('autoplay', '1');
  }
  return `https://www.youtube-nocookie.com/embed/${youtubeId}?${params.toString()}`;
}

export function buildYouTubeThumbnailUrl(youtubeId: string, quality: 'maxresdefault' | 'hqdefault' = 'maxresdefault'): string {
  return `https://i.ytimg.com/vi/${youtubeId}/${quality}.jpg`;
}
